import { recordError } from '@util/Errors';
import React, { ReactElement, useEffect, useRef } from 'react';
import { ArrayUtils, isEmpty } from 'ts-utils';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { ActionType } from '../../store/ActionTypes';
import { AppSelector, useAppDispatch, useAppStore } from '../../store/Store';
import { NavigationProps } from './FlowNavigation';
import {
  FinishAccountLayout,
  FinishAccountLayoutConfig,
  SubmitData,
} from './layouts/FinishAccountLayout';

export type FinishAccountScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.FinishAccount,
  FinishAccountLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  FinishAccountScreenConfig<ScreenNames>,
  ScreenNames
>;

export const FinishAccountScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const dispatch = useAppDispatch();
  const user = useAppStore(AppSelector.userState);

  const userId = user?.userId;
  // using useRef to capture the tempPassword, rather than it update when the user updates
  const tempPassword = useRef(user?.type === 'partial' ? user.tempPassword : undefined).current;
  const email = user?.email;

  useEffect(
    function navigateAuthenticatedUser() {
      // User has completed the signup when the user.type is no longer 'partial'
      if (user.type !== 'partial') {
        props.navigation.next(props, true);
      }
    },
    [user.type]
  );

  const isInInvalidState = isEmpty(userId) || isEmpty(email) || isEmpty(tempPassword);
  if (isInInvalidState) {
    const emptyFields = emptyValues({ userId, email, tempPassword });
    const message =
      'FinishAccountScreen: it should not be possible to get to' +
      ` this screen without ${ArrayUtils.toHumanReadableString(emptyFields)} being set`;
    recordError(new Error(message));
  }

  function handleSubmit(data: SubmitData) {
    if (isEmpty(userId)) {
      // This should not be possible, but the type guard helps with type checking
      throw new Error('Finish account setup can’t be finalized, userId is undefined');
    }

    const { name, password } = data;
    dispatch({
      type: ActionType.FINISH_TEMPORARY_REGISTER_USER,
      details: { name, password },
    });
  }

  return isInInvalidState ? (
    <p>An error occurred. Please contact support.</p>
  ) : (
    <FinishAccountLayout
      {...props.layout}
      name={user.name}
      email={email}
      onSubmit={handleSubmit}
      loading={user.loading}
      error={user.error?.message}
    />
  );
};

function emptyValues(values: { [key: string]: string | undefined }) {
  return Object.keys(values).filter((key) => isEmpty(values[key]));
}
